import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { pesquisaAPI } from '../../lib/api';
import { Opcoes, Moeda, UFS, mascaraMoeda, numeroDeMascara } from './PesquisaUI';

const inputCls = 'w-full rounded-[10px] border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';
const Rotulo = ({ children }) => <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{children}</label>;
const money = (v) => (v ? mascaraMoeda(String(Math.round(v))) : '');

const toForm = (r) => ({
  cidade: r.cidade || '', uf: r.uf || 'RR', unidade: r.unidade || '', vinculo: r.vinculo || '',
  horas: String(r.horas || ''), valor: money(r.valor), regime: r.regime || '', producao: r.producao || '',
  vale_para: r.vale_para || '', valor_feriado: money(r.valor_feriado),
  valor_apendicectomia: money(r.valor_apendicectomia), valor_acesso_venoso: money(r.valor_acesso_venoso),
  nome: r.nome || '', contato: r.contato || '', aceita_contato: !!r.aceita_contato,
});

export const EditarRespostaDialog = ({ resposta, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { setF(resposta ? toForm(resposta) : null); }, [resposta]);

  const salvar = async () => {
    const horas = parseInt(f.horas, 10);
    const valor = numeroDeMascara(f.valor);
    if (!f.cidade.trim() || !horas || !valor) {
      toast.error('Cidade, horas e valor são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      await pesquisaAPI.atualizar(resposta.id, {
        cidade: f.cidade.trim(), uf: f.uf, unidade: f.unidade, vinculo: f.vinculo, horas, valor,
        regime: f.regime, producao: f.producao, vale_para: f.vale_para,
        valor_feriado: numeroDeMascara(f.valor_feriado) || 0,
        valor_apendicectomia: numeroDeMascara(f.valor_apendicectomia) || 0,
        valor_acesso_venoso: numeroDeMascara(f.valor_acesso_venoso) || 0,
        nome: f.nome, contato: f.contato, aceita_contato: f.aceita_contato,
      });
      toast.success('Resposta atualizada');
      onSaved();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : d || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!resposta || !f) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="editar-resposta-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Editar resposta</DialogTitle>
          <DialogDescription>Corrija valores digitados errado. O valor por hora e as medianas são recalculados automaticamente.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-[1fr_88px] gap-2.5">
            <div><Rotulo>Cidade</Rotulo><input value={f.cidade} onChange={(e) => set('cidade', e.target.value)} className={inputCls} data-testid="editar-cidade-input" /></div>
            <div><Rotulo>UF</Rotulo><select value={f.uf} onChange={(e) => set('uf', e.target.value)} className={inputCls} data-testid="editar-uf-select">{UFS.map((u) => <option key={u}>{u}</option>)}</select></div>
          </div>
          <div><Rotulo>Unidade</Rotulo><Opcoes valor={f.unidade} onChange={(v) => set('unidade', v)} opcoes={['Hospital público', 'Hospital privado']} testPrefix="editar-unidade" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Rotulo>Vínculo</Rotulo><Opcoes valor={f.vinculo} onChange={(v) => set('vinculo', v)} opcoes={['PF', 'PJ']} testPrefix="editar-vinculo" /></div>
            <div><Rotulo>Regime</Rotulo><Opcoes valor={f.regime} onChange={(v) => set('regime', v)} opcoes={['Presencial', 'Sobreaviso']} testPrefix="editar-regime" /></div>
          </div>
          <div className="grid grid-cols-[110px_1fr] gap-2.5">
            <div><Rotulo>Horas</Rotulo><input inputMode="numeric" value={f.horas} onChange={(e) => set('horas', e.target.value.replace(/\D/g, '').slice(0, 2))} className={inputCls} data-testid="editar-horas-input" /></div>
            <div><Rotulo>Valor do plantão</Rotulo><Moeda valor={f.valor} onChange={(v) => set('valor', v)} testId="editar-valor-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Rotulo>Paga produção</Rotulo><Opcoes valor={f.producao} onChange={(v) => set('producao', v)} opcoes={['Sim', 'Não']} testPrefix="editar-producao" /></div>
            <div><Rotulo>Vale para</Rotulo><Opcoes valor={f.vale_para} onChange={(v) => set('vale_para', v)} opcoes={['Dia de semana', 'Feriado', 'Indiferente']} testPrefix="editar-vale" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div><Rotulo>Valor feriado</Rotulo><Moeda valor={f.valor_feriado} onChange={(v) => set('valor_feriado', v)} testId="editar-feriado-input" /></div>
            {f.producao === 'Sim' && (
              <>
                <div><Rotulo>Apendicectomia</Rotulo><Moeda valor={f.valor_apendicectomia} onChange={(v) => set('valor_apendicectomia', v)} testId="editar-apendicectomia-input" /></div>
                <div><Rotulo>Acesso venoso</Rotulo><Moeda valor={f.valor_acesso_venoso} onChange={(v) => set('valor_acesso_venoso', v)} testId="editar-acesso-venoso-input" /></div>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div><Rotulo>Nome</Rotulo><input value={f.nome} onChange={(e) => set('nome', e.target.value)} className={inputCls} data-testid="editar-nome-input" /></div>
            <div><Rotulo>Contato</Rotulo><input value={f.contato} onChange={(e) => set('contato', e.target.value)} className={inputCls} data-testid="editar-contato-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={f.aceita_contato} onChange={(e) => set('aceita_contato', e.target.checked)} className="accent-[#1e4e7e]" data-testid="editar-aceita-checkbox" />
            Aceita contato da Apex
          </label>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="editar-cancelar-button">Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="btn-primary" data-testid="editar-salvar-button">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
